// src/views/memory/MemoryView.tsx
import React, {
  useCallback, useEffect, useState, useRef, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { nanoid } from 'nanoid';
import { gsap } from 'gsap';
import './MemoryView.less';
import FixWidthContent from '../../components/FixWidthContent';
import Card from '../../components/Memory';
import { removeCard } from './slice/slice';
import { getActiveList, getUserData } from '../auth/slice/selectors';
import { Roles } from '../../constants/Roles';
import { EditListButton } from '../../components/EditListButton/EditListButton';
import useGoToRoute from '../../hooks/useGoToRoute';
import { Path } from '../../constants/Path';
import { BASE_API_URL } from '../../services/ApiService';
import withTagManager from '../../hooks/withTagManager';
import InfoButton from '../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import EndGameInfo from '../../components/EndGameInfo/EndgameInfo';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import { RightSideButtons } from '../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../components/UnsplashCredits/UnsplashCredits';
import { cleanTextForTTS } from '../../utils/textBracketSanitize';
import useActivityTracking from '../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../services/activity';

interface CardData {
  wordId: string;
  cardId: string;
  term: string;
  image: string;
  type: 'word' | 'image';
  isFlipped: boolean;
  isFinished: boolean;
}

const MAX_WORDS_PER_PART = 9; // Max 9 słów = 18 kart
const MATCH_DELAY = 1200;
const NEXT_PART_DELAY = 1500;

// Funkcja do tasowania array (Fisher-Yates)
const shuffleArray = <T extends unknown>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

const divideWordsIntoParts = (words: any[]): any[] => {
  // Tasuj słowa PRZED podziałem na części
  const shuffledWords = shuffleArray(words);
  const wordsCount = shuffledWords.length;

  // Jeśli <= 9 słów (18 kart), nie dziel
  if (wordsCount <= MAX_WORDS_PER_PART) {
    return [shuffledWords];
  }

  // Oblicz optymalną liczbę części
  const partsCount = Math.ceil(wordsCount / MAX_WORDS_PER_PART);

  // Oblicz bazowy rozmiar każdej części i resztę
  const baseSize = Math.floor(wordsCount / partsCount);
  const remainder = wordsCount % partsCount;

  // Podziel na możliwie równe części
  const dividedWords: any[] = [];
  let currentIndex = 0;

  for (let i = 0; i < partsCount; i += 1) {
    // Pierwsze 'remainder' części dostają +1 słowo
    const partSize = baseSize + (i < remainder ? 1 : 0);
    dividedWords.push(shuffledWords.slice(currentIndex, currentIndex + partSize));
    currentIndex += partSize;
  }

  return dividedWords;
};

const generateCardsForPart = (part: any[]): CardData[] => {
  const cards: CardData[] = [];

  part.forEach((word: any) => {
    cards.push({
      wordId: word.id,
      cardId: nanoid(),
      term: word.term,
      image: word.image,
      type: 'word',
      isFlipped: false,
      isFinished: false,
    });

    cards.push({
      wordId: word.id,
      cardId: nanoid(),
      term: word.term,
      image: word.image,
      type: 'image',
      isFlipped: false,
      isFinished: false,
    });
  });

  // Tasuj karty w ramach części
  return shuffleArray(cards);
};

function MemoryView() {
  const activeList = useSelector(getActiveList);
  const dispatch = useDispatch();
  const goToNext = useGoToRoute(Path.RECOGNIZE);
  const { play: playAudio, stop: stopAudio } = useAudioPlayer();

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  useActivityTracking(ActivityGame.MEMORY);

  // State
  const [activePartIndex, setActivePartIndex] = useState<number>(0);
  const [dividedWords, setDividedWords] = useState<any[]>([]);
  const [cards, setCardsList] = useState<CardData[]>([]);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [listEndInfo, setListEndInfo] = useState<boolean>(false);
  const [isInteractionDisabled, setIsInteractionDisabled] = useState<boolean>(false);
  const [partKey, setPartKey] = useState<string>(nanoid()); // Unikalny klucz dla każdej partii

  // Refs do zarządzania timerami i animacjami
  const timersRef = useRef<NodeJS.Timeout[]>([]);
  const cardsGridRef = useRef<HTMLDivElement>(null);

  // Utility do dodawania tracked timerów
  const addTimer = useCallback((callback: () => void, delay: number) => {
    const timer = setTimeout(() => {
      callback();
      // Usuń z listy po wykonaniu
      timersRef.current = timersRef.current.filter((t) => t !== timer);
    }, delay);
    timersRef.current.push(timer);

    return timer;
  }, []);

  // Cleanup wszystkich timerów
  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current = [];
  }, []);

  // Cleanup przy unmount
  useEffect(() => () => {
    clearAllTimers();
    stopAudio();
  }, [clearAllTimers, stopAudio]);

  // Funkcja do czyszczenia aktualnego matcha (używa functional update)
  const clearActualMatch = useCallback(() => {
    setCardsList((prevCards) => prevCards.map((card) => ({
      ...card,
      isFlipped: false,
    })));
    setIsInteractionDisabled(false);
  }, []);

  // Handler kliknięcia karty
  const handleClick = useCallback((cardId: string) => {
    setCardsList((prevCards) => {
      // Sprawdź czy karta już flipped lub finished
      const currentCard = prevCards.find((card) => card.cardId === cardId);
      if (!currentCard || currentCard.isFlipped || currentCard.isFinished) {
        return prevCards;
      }

      // Sprawdź ile kart jest już flipped (bez tej właśnie klikniętej)
      const alreadyFlipped = prevCards.filter((card) => card.isFlipped);

      // Jeśli już są 2 karty flipped, zignoruj kliknięcie
      if (alreadyFlipped.length >= 2) {
        return prevCards;
      }

      // Wyczyść poprzednie timery
      clearAllTimers();

      // Odtwórz audio dla image cards
      if (currentCard.type === 'image') {
        const cleanTerm = cleanTextForTTS(currentCard.term);
        const audioUrl = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`;
        playAudio(audioUrl).catch((err) => console.warn('Audio playback failed:', err));
      }

      // Flip aktualną kartę
      const newCards = prevCards.map((card) => (card.cardId === cardId
        ? { ...card, isFlipped: true }
        : card));

      // Sprawdź stan po flip
      const flippedCards = newCards.filter((card) => card.isFlipped);

      if (flippedCards.length === 2) {
        // Dwie karty są flipped
        const [first, second] = flippedCards;

        if (first.wordId === second.wordId) {
          // MATCH! ✅
          setIsInteractionDisabled(true);

          addTimer(() => {
            setCardsList((currentCards) => {
              const updatedCards = currentCards.map((card) => (card.wordId === first.wordId
                ? { ...card, isFlipped: false, isFinished: true }
                : card));

              // Sprawdź czy to była ostatnia para
              const notFinished = updatedCards.filter((card) => !card.isFinished);
              if (notFinished.length === 0) {
                setIsFinished(true);
              }

              return updatedCards;
            });
            setIsInteractionDisabled(false);
          }, MATCH_DELAY);
        } else {
          // NIE PASUJĄ ❌
          setIsInteractionDisabled(true);
          addTimer(clearActualMatch, MATCH_DELAY);
        }
      }

      return newCards;
    });
  }, [addTimer, clearActualMatch, clearAllTimers, playAudio]);

  // Próba to odkrycie drugiej karty: para albo nie. Rozstrzyga się w aktualizacji stanu kart (handleClick), której
  // React może użyć więcej niż raz — dlatego próbę zapisuje efekt, raz na każdą parę odkrytych kart
  const recordedPairRef = useRef<string | null>(null);

  useEffect(() => {
    const flippedCards = cards.filter((card) => card.isFlipped);

    if (flippedCards.length !== 2) {
      recordedPairRef.current = null;

      return;
    }

    const [first, second] = flippedCards;
    const pairKey = `${first.cardId}:${second.cardId}`;

    if (recordedPairRef.current === pairKey) return;

    recordedPairRef.current = pairKey;
    recordAnswer(first.wordId === second.wordId);
  }, [cards]);

  // Handler double click (jeśli potrzebny)
  const handleDoubleClick = useCallback((cardId: string) => {
    dispatch(removeCard({
      type: 'removeCard',
      id: cardId,
    }));
  }, [dispatch]);

  // Inicjalizacja gry przy załadowaniu
  useEffect(() => {
    if (!activeList?.words?.length) return;

    const { words } = activeList;

    // Zawsze używaj divideWordsIntoParts - funkcja sama zdecyduje czy dzielić
    const divided = divideWordsIntoParts(words);
    setDividedWords(divided);
    const firstCards = generateCardsForPart(divided[0]);
    setCardsList(firstCards);

    setActivePartIndex(0);
    setIsFinished(false);
    setListEndInfo(false);
    setPartKey(nanoid());
  }, [activeList?.words]);

  // Obsługa przejścia do następnej części lub końca gry
  useEffect(() => {
    if (!isFinished) return;

    if (activePartIndex < dividedWords.length - 1) {
      // Przejdź do następnej części
      addTimer(() => {
        setIsFinished(false);
        const newIndex = activePartIndex + 1;
        setActivePartIndex(newIndex);
        const newPart = dividedWords[newIndex];
        const newCards = generateCardsForPart(newPart);
        setCardsList(newCards);
        setPartKey(nanoid()); // Nowy klucz dla nowej partii
      }, NEXT_PART_DELAY);
    } else {
      // Koniec wszystkich części
      addTimer(() => {
        setListEndInfo(true);
      }, NEXT_PART_DELAY);
    }
  }, [isFinished, activePartIndex, dividedWords, addTimer]);

  // 🎨 GSAP Animation - Magnetic Gather
  useEffect(() => {
    if (
      cards.length > 0
      && !listEndInfo
      && !isFinished
      && cardsGridRef.current
    ) {
      // Poczekaj na pełne wyrenderowanie DOM
      const animationTimer = setTimeout(() => {
        const cardElements = cardsGridRef.current?.children;
        if (cardElements && cardElements.length > 0) {
          gsap.fromTo(
            Array.from(cardElements),
            {
              opacity: 0,
              scale: 0.2,
              x: () => gsap.utils.random(-400, 400),
              y: () => gsap.utils.random(-300, 300),
              rotation: () => gsap.utils.random(-180, 180),
            },
            {
              opacity: 1,
              scale: 1,
              x: 0,
              y: 0,
              rotation: 0,
              duration: 1,
              ease: 'expo.out',
              stagger: {
                amount: 0.4,
                from: 'random',
              },
            },
          );
        }
      }, 100);

      return () => clearTimeout(animationTimer);
    }

    return undefined;
  }, [partKey, listEndInfo, isFinished]);

  // Funkcja restart gry
  const repeatGame = useCallback(() => {
    clearAllTimers();
    stopAudio();

    setIsFinished(false);
    setListEndInfo(false);
    setActivePartIndex(0);
    setIsInteractionDisabled(false);

    // Wygeneruj nowe rundy z NOWYM tasowaniem!
    const { words } = activeList;
    const newDividedWords = divideWordsIntoParts(words); // Nowe tasowanie i inteligentny podział!
    setDividedWords(newDividedWords);
    const firstCards = generateCardsForPart(newDividedWords[0]);
    setCardsList(firstCards);

    setPartKey(nanoid()); // Nowy klucz dla restartu
  }, [activeList?.words, clearAllTimers, stopAudio]);

  // Memoizuj karty do renderowania
  const cardsToRender = useMemo(() => cards, [cards]);

  // Lista z 1–2 słowami daje tylko 2 albo 4 karty — mniej niż kolumn siatki (do 6), a puste kolumny zostawałyby
  // po prawej. Wtedy kolumn najwyżej tyle, ile kart (klasa .columnsN w MemoryView.less), i plansza stoi na środku.
  // Odnalezione pary zostają na planszy, więc w trakcie gry liczba kolumn się nie zmienia
  const columnsClass = cards.length === 2 || cards.length === 4 ? `columns${cards.length}` : '';

  return (
    <>
      <FixWidthContent className={`test ${listEndInfo ? '' : 'boardCentered'}`}>
        {dividedWords.length >= 2 && (
          <p id="pages">
            {activePartIndex + 1} / {dividedWords.length}
          </p>
        )}

        {!listEndInfo && (
          <div>
            <div className={`cardsGrid ${columnsClass}`} ref={cardsGridRef}>
              {cardsToRender.map((card: CardData) => (
                <Card
                  id={card.cardId}
                  text={card.term}
                  image={card.image}
                  type={card.type}
                  isFinished={card.isFinished}
                  isFlipped={card.isFlipped}
                  isDisabled={isInteractionDisabled}
                  handleClick={handleClick}
                  handleDoubleClick={handleDoubleClick}
                  key={card.cardId}
                />
              ))}
            </div>
          </div>
        )}

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} />
        )}

        {!listEndInfo && <UnsplashCredits words={dividedWords[activePartIndex]} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
}

export default withTagManager(MemoryView);
